import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Input from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { number, object, string } from "yup";
import {
  useCreateWithdraw,
  useGetOpeningAmount,
  useGetWithdrawData,
  useUpdateWithdraw,
} from "../api-clients";

const FORM_FIELDS = {
  DATE: "date",
  OPENING: "opening",
  WITHDRAW: "withdraw",
  CLOSING: "closing",
  REMARKS: "remarks",
};

const CreateWithdraw = ({
  isInViewMode = false,
}: {
  isInViewMode?: boolean;
}) => {
  useSetActiveTab(TAB_NAMES.WITHDRAW);
  const navigate = useNavigate();
  const { withdrawId: id } = useParams();

  const withdrawId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!withdrawId, [withdrawId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetOpeningAmountLoading, data: openingAmountData } =
    useGetOpeningAmount();

  const initialOpeningAmount = openingAmountData?.data?.closing ?? null;

  const { isLoading: isGetWithdrawLoading, data } =
    useGetWithdrawData(withdrawId);

  const expenseData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateUser,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateWithdraw(withdrawId);

  const { isLoading, isSuccess, execute, error } = useCreateWithdraw();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.DATE]: expenseData?.[FORM_FIELDS.DATE] ?? null,
      [FORM_FIELDS.OPENING]:
        expenseData?.[FORM_FIELDS.OPENING] ?? initialOpeningAmount,
      [FORM_FIELDS.WITHDRAW]: expenseData?.[FORM_FIELDS.WITHDRAW] ?? null,
      [FORM_FIELDS.CLOSING]: expenseData?.[FORM_FIELDS.CLOSING] ?? null,
      [FORM_FIELDS.REMARKS]: expenseData?.[FORM_FIELDS.REMARKS] ?? "",
    },
    validationSchema: object({
      [FORM_FIELDS.DATE]: string().required("This is a Required field."),
      [FORM_FIELDS.OPENING]: number()
        .nullable()
        .required("This is a Required field."),
      [FORM_FIELDS.WITHDRAW]: number()
        .nullable()
        .required("This is a Required field."),
      [FORM_FIELDS.CLOSING]: number()
        .nullable()
        .required("This is a Required field."),
      [FORM_FIELDS.REMARKS]: string().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateUser(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Withdraw Updated" : "Withdraw Created."
      );
      navigate("/store/withdraw");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () =>
      isLoading ||
      isGetWithdrawLoading ||
      isUpdateLoading ||
      isGetOpeningAmountLoading,
    [
      isGetWithdrawLoading,
      isLoading,
      isUpdateLoading,
      isGetOpeningAmountLoading,
    ]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const openingAmount = values[FORM_FIELDS.OPENING];

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Withdraw" showBreadcrumb />
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <DatePicker
            label="Date"
            value={values[FORM_FIELDS.DATE]}
            onChange={(value) => setFieldValue(FORM_FIELDS.DATE, value)}
            disabled={isFieldDisabled}
            error={getFieldError(FORM_FIELDS.DATE)}
            required
          />

          <Input
            type="number"
            label="Opening"
            placeholder="Opening"
            name={FORM_FIELDS.OPENING}
            value={openingAmount as string}
            error={getFieldError(FORM_FIELDS.OPENING)}
            onChange={(e) => setFieldValue(FORM_FIELDS.OPENING, e.target.value)}
            disabled={isFieldDisabled}
            required
          />

          <Input
            type="number"
            label="Withdraw"
            placeholder="Withdraw"
            name={FORM_FIELDS.WITHDRAW}
            value={values[FORM_FIELDS.WITHDRAW] as string}
            error={getFieldError(FORM_FIELDS.WITHDRAW)}
            onChange={(e) => {
              const closingAmount = Number(e.target.value);
              setFieldValue(FORM_FIELDS.WITHDRAW, closingAmount);
              setFieldValue(
                FORM_FIELDS.CLOSING,
                openingAmount - Number(closingAmount)
              );
            }}
            disabled={isFieldDisabled}
            required
          />

          <Input
            type="number"
            label="Closing"
            placeholder="Closing"
            name={FORM_FIELDS.CLOSING}
            value={values[FORM_FIELDS.CLOSING] as string}
            error={getFieldError(FORM_FIELDS.CLOSING)}
            onChange={(e) => {}}
            disabled
            required
          />

          <Input
            label="Remarks"
            placeholder="Remarks"
            name={FORM_FIELDS.REMARKS}
            value={values[FORM_FIELDS.REMARKS] as string}
            error={getFieldError(FORM_FIELDS.REMARKS)}
            onChange={(e) => setFieldValue(FORM_FIELDS.REMARKS, e.target.value)}
            disabled={isFieldDisabled}
            required
          />
        </div>
        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateWithdraw;
